import { useEffect, useState } from "react";
import i18next from "i18next";
import { initReactI18next } from "react-i18next";
import { useLocation } from "react-router-dom";
import { TitleI18nKey, DescI18nKey } from "./config";
import { firstLetterCap } from "@utils/util";
import { useAppSelector } from "@hooks/redux";
import { CDN_ASSETS_URL } from "@config";

i18next.use(initReactI18next).init({
  resources: {},
  fallbackLng: "en-US",
  interpolation: { escapeValue: false },
});

export const useI18nLanguage = (language: string) => {
  const location = useLocation();
  const { region } = useAppSelector((state) => state.globalReducer);
  const { regionOptions } = useAppSelector((state) => state.configReducer);
  const [updateKey, setUpdateKey] = useState(0);

  const updateLanguage = () => {
    i18next.changeLanguage(language);

    const defaultTitle = i18next.t("Kaloclip: All-in-One AI Video & Image Generator for E-commerce");
    const defaultDesc = i18next.t(
      "Decode viral DNA and create high-converting e-commerce content in seconds. KaloClip is the ultimate AI toolkit to reverse-engineer scripts and replicate success across all platforms, including TikTok, Amazon, and Shopee.",
    );

    const titleI18nKey = TitleI18nKey[location.pathname];
    const descI18nKey = DescI18nKey[location.pathname];
    const regionName = regionOptions.find((regionOption) => regionOption.value === region)?.name || "";

    let title = i18next.t(titleI18nKey, {
      type: i18next.t(firstLetterCap(location.pathname.slice(1))),
      region: i18next.t(regionName),
    });

    let desc = i18next.t(descI18nKey, {
      type: i18next.t(firstLetterCap(location.pathname.slice(1))),
      region: i18next.t(regionName),
    });

    if (titleI18nKey && title !== titleI18nKey) {
      document.title = title;
    } else {
      document.title = defaultTitle;
    }

    // 更新 meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.setAttribute("name", "description");
      document.head.appendChild(metaDesc);
    }

    if (descI18nKey && desc !== descI18nKey) {
      metaDesc.setAttribute("content", desc);
    } else {
      metaDesc.setAttribute("content", defaultDesc);
    }

    // 更新 og:title (用于社交媒体分享预览)
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement("meta");
      ogTitle.setAttribute("property", "og:title");
      document.head.appendChild(ogTitle);
    }
    ogTitle.setAttribute("content", titleI18nKey && title !== titleI18nKey ? title : defaultTitle);

    // 更新 og:description (用于社交媒体分享预览)
    let ogDesc = document.querySelector('meta[property="og:description"]');
    if (!ogDesc) {
      ogDesc = document.createElement("meta");
      ogDesc.setAttribute("property", "og:description");
      document.head.appendChild(ogDesc);
    }
    ogDesc.setAttribute("content", descI18nKey && desc !== descI18nKey ? desc : defaultDesc);
  };

  const requestI18nVersion = async () => {
    if (!window.kalo_transition[language]) {
      let fileName = window.i18n_version || "i18n.js";

      // build-time hash injection fails in local dev; fall back to runtime fetch via vite proxy
      if (fileName === "i18n.js") {
        try {
          const res = await fetch("/api/i18nVersion");
          const json = await res.json();
          if (json?.data) {
            fileName = json.data;
            window.i18n_version = fileName;
          }
        } catch {
          // keep "i18n.js" as filename; request will 403 but won't block rendering
        }
      }

      const script = document.createElement("script");
      script.src = `${CDN_ASSETS_URL}/production/kaloclip_languages/${language}/${fileName}`;
      script.async = true;
      script.onload = () => {
        i18next.addResourceBundle(language, "tiktok", window.kalo_transition[language].translation);
        setUpdateKey((prev) => prev + 1);
      };

      document.body.appendChild(script);
    } else {
      setUpdateKey((prev) => prev + 1);
    }
  };

  useEffect(() => {
    if (language) {
      requestI18nVersion();
    }
  }, [language]);

  useEffect(() => {
    updateLanguage();
  }, [location, language, region, regionOptions, updateKey]);
};
